/**
 * i18n init — react-i18next with browser + querystring language detection.
 *
 * Languages match the marketing landing 1:1:
 *   en, ru, uk (shown as "UA"), de, es, pl, cs, lt, lv.
 *
 * Landing handoff: the landing links into the app with ?lang=<code> (e.g.
 * app.dathent.ai/...?lang=de or ?lang=ua). resolveInitialLang() reads it,
 * normalizes 'ua' -> 'uk' + region forms (de-DE -> de), persists to
 * localStorage, and seeds i18next so the user keeps the same language they
 * picked on the landing. Without a ?lang= it falls back to the in-app
 * switcher's stored choice, then the browser language.
 *
 * Storage key 'dathent-lang' aligns with the LangSwitch component.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en/common.json';
import ru from './locales/ru/common.json';
import uk from './locales/uk/common.json';
import de from './locales/de/common.json';
import es from './locales/es/common.json';
import pl from './locales/pl/common.json';
import cs from './locales/cs/common.json';
import lt from './locales/lt/common.json';
import lv from './locales/lv/common.json';
import sv from './locales/sv/common.json';

const SUPPORTED = ['en', 'ru', 'uk', 'de', 'es', 'pl', 'cs', 'lt', 'lv', 'sv'];
const STORAGE_KEY = 'dathent-lang';
// Set once the user makes an EXPLICIT language choice — either the in-app
// switcher or a landing ?lang= handoff. While set, applyBrandLanguage() (the
// brand-language auto-sync) is a no-op, so a deliberate pick is never silently
// overridden by the workspace's brand language on the next load.
const EXPLICIT_KEY = 'dathent-lang-explicit';

export function normalizeLang(raw) {
  if (!raw || typeof raw !== 'string') return undefined;
  const base = raw.toLowerCase().split('-')[0];
  const code = base === 'ua' ? 'uk' : base;
  return SUPPORTED.includes(code) ? code : undefined;
}

// Map a brand-profile language to a supported UI code. Unlike normalizeLang
// (strict ISO, used for the landing ?lang= handoff), this tolerates the raw
// human-written values brand profiles hold — an English name ("Ukrainian"), a
// NATIVE name ("Українська", "Русский" — exactly what the switcher displays), or
// a legacy multi-language string ("Ukrainian / English"). Each language is a set
// of stems: short codes (<4 chars) match a whole token exactly; longer name
// stems match a token PREFIX, so inflected native forms ("русский", "українською")
// resolve. We tokenize Unicode-aware (\p{L}) instead of ASCII \b — \b never
// matches Cyrillic, which would silently kill the RU/UA case this exists for.
const BRAND_LANG_STEMS = [
  ['en', ['english', 'англ', 'en']],
  ['uk', ['ukrainian', 'українськ', 'укр', 'uk', 'ua']],
  ['ru', ['russian', 'русск', 'ru']],
  ['de', ['german', 'deutsch', 'німецьк', 'немецк', 'de']],
  ['es', ['spanish', 'español', 'espanol', 'іспан', 'испан', 'es']],
  ['pl', ['polish', 'polski', 'польськ', 'польск', 'pl']],
  ['cs', ['czech', 'čeština', 'cestina', 'чеськ', 'чеш', 'cs']],
  ['lt', ['lithuanian', 'lietuv', 'литов', 'lt']],
  ['lv', ['latvian', 'latviešu', 'latvie', 'латв', 'lv']],
  ['sv', ['swedish', 'svenska', 'швед', 'sv']],
];
function brandLangToCode(raw) {
  const strict = normalizeLang(raw);
  if (strict) return strict;
  if (!raw || typeof raw !== 'string') return undefined;
  // Split on any non-letter (Unicode-aware) so Cyrillic and Latin tokens are
  // isolated the same way; \p{L}+/u is supported far more widely than lookbehind.
  const tokens = raw.toLowerCase().split(/[^\p{L}]+/u).filter(Boolean);
  // Collect every distinct supported language mentioned. We only auto-apply when
  // EXACTLY ONE is recognized — zero or several (ambiguous legacy multi-language
  // strings) → bail and leave the UI untouched rather than guess wrong.
  const matched = new Set();
  for (const tok of tokens) {
    for (const [code, stems] of BRAND_LANG_STEMS) {
      if (stems.some((s) => (s.length < 4 ? tok === s : tok.startsWith(s)))) {
        matched.add(code);
      }
    }
  }
  return matched.size === 1 ? [...matched][0] : undefined;
}

// Read ?lang= the landing hands over, normalize, persist, and return it so it
// wins over the detector. Returns undefined when there's no usable param (then
// the detector's localStorage/navigator order applies).
function resolveInitialLang() {
  try {
    const code = normalizeLang(new URLSearchParams(window.location.search).get('lang'));
    if (code) {
      localStorage.setItem(STORAGE_KEY, code);
      // A landing handoff is a deliberate pick — lock it in against brand sync.
      localStorage.setItem(EXPLICIT_KEY, '1');
      return code;
    }
  } catch {
    /* no window (SSR) or blocked storage — fall through to the detector */
  }
  return undefined;
}

/**
 * Rescue users "stuck" on English: a RU/UA user on an English browser who
 * never picked a language sees the app in English even though their whole
 * workspace (brand profile) is Ukrainian. When that happens, switch the UI once
 * to the brand language (e.g. UA brand -> UA app).
 *
 * Guards (all must hold):
 *  - No EXPLICIT choice on record (switcher pick or ?lang= handoff) — those
 *    always win and are never overridden.
 *  - The current UI is English (the fallback / an English browser default).
 *    Users already on a NON-English UI are left untouched — that's a deliberate
 *    or browser-matched choice, not a "stuck on English" situation.
 *  - The brand resolves to exactly one supported, non-English language
 *    (brandLangToCode bails on empty/unknown/ambiguous multi-language values).
 *
 * Call it after the brand profile loads. Returns the applied code, else undefined.
 */
export function applyBrandLanguage(brandLang) {
  try {
    if (localStorage.getItem(EXPLICIT_KEY) === '1') return undefined;
    const code = brandLangToCode(brandLang);
    if (!code || code === i18n.resolvedLanguage) return undefined;
    // Only act when the user is currently on English — never flip a non-English UI.
    const current = (i18n.resolvedLanguage || i18n.language || '').split('-')[0];
    if (current !== 'en') return undefined;
    localStorage.setItem(STORAGE_KEY, code);
    i18n.changeLanguage(code);
    return code;
  } catch {
    return undefined;
  }
}

// Record an explicit user pick (called by the in-app switcher) so brand sync
// stops overriding it. Pairs with applyBrandLanguage's guard.
export function markExplicitLanguageChoice() {
  try {
    localStorage.setItem(EXPLICIT_KEY, '1');
  } catch {
    /* blocked storage — degrade to detector defaults */
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    lng: resolveInitialLang(),
    supportedLngs: SUPPORTED,
    nonExplicitSupportedLngs: true,
    ns: ['common'],
    defaultNS: 'common',
    resources: {
      en: { common: en },
      ru: { common: ru },
      uk: { common: uk },
      de: { common: de },
      es: { common: es },
      pl: { common: pl },
      cs: { common: cs },
      lt: { common: lt },
      lv: { common: lv },
      sv: { common: sv },
    },
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: STORAGE_KEY,
      caches: ['localStorage'],
    },
    returnNull: false,
  });

export default i18n;
